<template>
  <modal
    name="modal--generate-report"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="450"
    :height="'auto'"
    :adaptive="true"
    :reset="true"
    :scrollable="true"
    draggable=".drag-handler"
    class="py-10 z-50"
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <div
      class="flex flex-col items-center text-black h-full overflow-hidden w-full bg-white rounded-sm justify-between p-4"
    >
      <div
        class="mt-4 text-center text-4xl font-extralight tracking-tight leading-none drag-handler"
      >
        Weekly Report Inputs
      </div>
      <div class="mt-2 w-4/5 text-center">
        <select
          class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
          id="date"
          v-model="date"
          @click.stop
        >
          <option value="" disabled selected>selection required...</option>
          <option v-for="i in dates" :key="`${i}-date`" :value="i">
            {{ dateRange(i) }}
          </option>
        </select>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
        >
          <svg
            class="fill-current h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
            />
          </svg>
        </div>
      </div>

      <div class="text-xl">
        <form
          @submit.prevent="handleConfirm"
          @reset="hideModal"
          autocomplete="off"
        >
          <div
            class="mt-4 text-center text-3xl font-extralight tracking-tight drag-handler"
          >
            Sales
          </div>
          <div class="flex flex-row justify-between mb-1">
            <label class="w-1/3 justify-center flex items-center"> </label>
            <label class="w-1/3 justify-center flex items-center">
              In-store
            </label>
            <label class="w-1/3 justify-center flex items-center">
              Delivery
            </label>
          </div>
          <div
            class="flex flex-row justify-between mb-1"
            v-for="(label, index) in days"
            :key="`Sales_${index}`"
          >
            <label
              class="w-1/3 justify-center pr-4 flex items-center"
              :for="label"
            >
              {{ label | capitalize }}
            </label>
            <input
              class="mx-1 w-1/3 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              autocomplete="off"
              v-model="data.salesInStore[index]"
            />
            <input
              class="mx-1 w-1/3 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              autocomplete="off"
              v-model="data.salesDelivery[index]"
            />
          </div>
          <div class="mt-4 flex flex-row justify-between mb-1">
            <label class="w-2/3 justify-center pr-4 flex items-center truncate">
              Average Delivery Fees
            </label>
            <div
              class="mx-1 w-1/3 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 pr-10 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            >
              <input
                class="appearance-none bg-transparent text-center leading-tight w-full"
                autocomplete="off"
                v-model="data.feesDelivery"
              />
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 font-bold"
              >
                %
              </div>
            </div>
          </div>

          <div
            class="mt-4 text-center text-3xl font-extralight tracking-tight drag-handler"
          >
            Wages
          </div>
          <div class="flex flex-row justify-between mb-1">
            <label class="w-1/3 justify-center flex items-center"> </label>
            <label class="w-1/3 justify-center flex items-center">
              Management
            </label>
            <label class="w-1/3 justify-center flex items-center">
              Hourly
            </label>
          </div>
          <div
            class="flex flex-row justify-between mb-1"
            v-for="(label, index) in days"
            :key="`Wages_${index}`"
          >
            <label
              class="w-1/3 justify-center pr-4 flex items-center truncate"
              :for="label"
            >
              {{ label | capitalize }}
            </label>

            <input
              class="mx-1 w-1/3 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              autocomplete="off"
              v-model="data.payrollManagement[index]"
            />
            <input
              class="mx-1 w-1/3 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              autocomplete="off"
              v-model="data.payrollHourly[index]"
            />
          </div>

          <div class="mt-4 flex flex-row justify-between mb-1">
            <label class="w-2/3 justify-center pr-4 flex items-center truncate">
              Payroll Taxes & Benefits
            </label>
            <div
              class="mx-1 w-1/3 text-center text-base font-normal normal-case focus:outline-none focus:shadow-outline appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 pr-10 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            >
              <input
                class="appearance-none bg-transparent text-center leading-tight w-full"
                autocomplete="off"
                v-model="data.feesPayroll"
              />
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 font-bold"
              >
                %
              </div>
            </div>
          </div>

          <div class="button-set flex flex-row justify-between mt-10">
            <button
              class="buttonOutline focus:shadow-outline focus:outline-none"
              type="reset"
            >
              Cancel
            </button>
            <button
              class="buttonOutline focus:shadow-outline focus:outline-none"
              @click.prevent="getApis()"
            >
              {{ loading ? 'loading..' : 'Populate' }}
            </button>

            <button
              class="button focus:shadow-outline focus:outline-none"
              type="submit"
            >
              {{ id ? 'Save' : 'Generate' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { days } from '@/const.js'
import { floatify } from '@/utils.js'
import { DateTime } from 'luxon'

export default {
  name: 'ModalGenerateReport',
  data() {
    return {
      days,
      id: null,
      date: null,
      loading: false,
      data: {
        salesInStore: [],
        salesDelivery: [],
        guestsInStore: [],
        guestsDelivery: [],
        payrollHourly: [],
        payrollManagement: [],
        feesPayroll: null,
        feesDelivery: null
      }
    }
  },
  computed: {
    ...mapGetters('storeModule', ['deliveryFee', 'payrollTax']),
    ...mapGetters('storeModule/reports', ['reports', 'reportById']),
    dates() {
      return Array.from({ length: 8 }, (v, k) =>
        DateTime.utc()
          .setZone('America/New_York')
          .startOf('week')
          .minus({ weeks: k })
          .toJSDate()
      )
    }
  },
  methods: {
    ...mapActions('config', ['setFabVisible']),
    ...mapActions('storeModule/reports', ['gatherAPI']),
    ...mapActions('storeModule', ['generateReport']),
    handleConfirm: function() {
      this.id
        ? this.generateReport({
            ...this.data,
            id: this.id,
            date: this.date
          })
        : this.generateReport({ ...this.data, date: this.date })
      this.hideModal()
    },
    hideModal: function() {
      this.$modal.hide('modal--generate-report')
    },
    getApis: async function() {
      if (!this.date) return

      this.loading = true

      let dateString = DateTime.fromJSDate(this.date)
        .setZone('America/New_York')
        .startOf('week')
        .minus({ days: 1 })
        .toFormat('yyyy-MM-dd')

      let info = await this.gatherAPI({ date: dateString })
      console.log(this.date, dateString, info.data)

      for (let i = 0; i < 7; i++) {
        this.$set(this.data.salesInStore, i, info.data[6 - i].instoreSales)
        this.$set(this.data.salesDelivery, i, info.data[6 - i].deliverySales)
        this.$set(
          this.data.payrollManagement,
          i,
          info.data[6 - i].managementCost
        )
        this.$set(this.data.payrollHourly, i, info.data[6 - i].staffCost)
      }
      this.$set(this.data, 'feesPayroll', this.payrollTax)
      this.data.feesPayroll = this.payrollTax
      this.$set(this.data, 'feesDelivery', this.deliveryFee)
      this.data.feesDelivery = this.deliveryFee

      this.loading = false
    },
    dateRange(date) {
      return date
        ? `${DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('week')
            .minus({ weeks: 1 })
            .toFormat('LLL dd')} - ${DateTime.fromJSDate(date)
            .setZone('America/New_York')
            .startOf('week')
            .minus({ days: 1 })
            .toFormat('LLL dd')}`
        : `${DateTime.utc()
            .setZone('America/New_York')
            .startOf('week')
            .minus({ days: 1 })
            .format('MMM Do')}`
    },
    beforeOpen(event) {
      // If an ID for a product was passed, get that product info, if not, set blank
      if (
        event &&
        event.params &&
        event.params.id &&
        this.reports[event.params.id]
      ) {
        // create a local copy, not a reference, for temporary editing
        this.id = event.params.id
        this.date = DateTime.fromSeconds(
          this.reports[event.params.id].date.seconds
        )
          .setZone('America/New_York')
          .startOf('day')
          .toJSDate()
        // change day to week??
        this.data.salesInStore = [
          ...this.reports[event.params.id].sales.inStore
        ]
        this.data.salesDelivery = [
          ...this.reports[event.params.id].sales.delivery
        ]
        this.data.guestsInStore = [
          ...this.reports[event.params.id].guests.inStore
        ]
        this.data.guestsDelivery = [
          ...this.reports[event.params.id].guests.delivery
        ]
        this.data.payrollHourly = [
          ...this.reports[event.params.id].payroll.hourly
        ]
        this.data.payrollManagement = [
          ...this.reports[event.params.id].payroll.management
        ]
        this.data.feesPayroll = this.payrollTax
        this.data.feesPayroll =
          Math.abs(this.reports[event.params.id].payroll.fees) > 1
            ? Math.abs(this.reports[event.params.id].payroll.fees)
            : floatify(
                Math.abs(this.reports[event.params.id].payroll.fees) * 100
              )
        this.data.feesDelivery = this.deliveryFee
        this.data.feesDelivery =
          Math.abs(this.reports[event.params.id].sales.fees) > 1
            ? Math.abs(this.reports[event.params.id].sales.fees)
            : floatify(Math.abs(this.reports[event.params.id].sales.fees) * 100)
      } else {
        this.date = this.dates[0]
      }
      this.setFabVisible(false)
    },
    beforeClose() {
      this.id = null
      this.date = null
      this.data.salesInStore = []
      this.data.salesDelivery = []
      this.data.guestsInStore = []
      this.data.guestsDelivery = []
      this.data.payrollHourly = []
      this.data.payrollManagement = []
      this.data.feesPayroll = this.payrollTax
      this.data.feesDelivery = this.deliveryFee
      this.setFabVisible(true)
    }
  }
}
</script>
